import * as React from "react"
import { Box, Button } from "@mui/material"
import Head from "next/head"
import { useRouter } from "next/router"
import { ErrorBoundary } from "react-error-boundary"
import toast from "react-hot-toast"
import * as Sentry from "@sentry/nextjs"
import { HiExclamationCircle, HiOutlineX } from "react-icons/hi"
import { useQueryClient } from "react-query"

import ClientOnly from "components/ClientOnly"
import DashboardSidebar from "components/DashboardSidebar"
import DashboardMobileNav from "components/DashboardMobileNav"
import PageLoader from "components/PageLoader"
import DashboardFooter from "components/DashboardFooter"
import EmptyMessage from "components/EmptyMessage"
import { hasPlanFeatures } from "components/PlanGate"
import { useAuth } from "context/auth"
import {
  useCurrentUser,
  useLogUserSession,
  useUpdateConfirmUser,
} from "context/db"
import { useSiteSettings } from "context/siteSettings"
import { configGtag } from "util/google-analytics"
import { configFullstory } from "util/fullstory"
import { getRawDate, getPastDate } from "util/formatters"

export default function DashboardLayout({ children }) {
  const { isAuthenticated, isImpersonating, impersonation, setImpersonation } =
    useAuth()
  const { data: currentUser } = useCurrentUser()
  const queryClient = useQueryClient()
  const userEmail = currentUser?.email || ""
  const userId = currentUser?.id || ""
  const userConfirmed = currentUser?.confirmed || false
  const userGroupId = currentUser?.group?.id || ""
  const { mutate: logUserSession } = useLogUserSession()
  const { mutate: updateUser } = useUpdateConfirmUser()

  // values to determine if dates can be set from the url
  const router = useRouter()
  const isOnStaticPage = router?.pathname?.startsWith(
    "/dashboard/companies/[id]/static"
  )
  const { setSelectedStartDate, setSelectedEndDate } = useSiteSettings()
  const startDateFromUrl = router?.query?.sd
  const endDateFromUrl = router?.query?.ed
  const hasHistoricalDataPermissions = hasPlanFeatures({
    user: currentUser,
    allowedFeatures: ["historicalData"],
  })
  const historicalStartDateMin = React.useMemo(
    () => getRawDate(getPastDate(31, "day")),
    []
  )
  // set start and end dates based on the URL provided
  React.useEffect(() => {
    if (!startDateFromUrl || !endDateFromUrl) {
      return
    }

    const rawStartDate = getRawDate(startDateFromUrl)
    const rawEndDate = getRawDate(endDateFromUrl)
    if (rawStartDate.valueOf() >= rawEndDate.valueOf()) {
      console.warn("provided dates from URL are invalid")
    } else if (!rawStartDate.isValid()) {
      console.warn("the provided start dates from URL is invalid")
    } else if (!rawEndDate.isValid()) {
      console.warn("the provided start dates from URL is invalid")
    } else if (
      !hasHistoricalDataPermissions &&
      rawStartDate.valueOf() < historicalStartDateMin.valueOf()
    ) {
      console.warn(
        "the start date is too early, historical data is not allowed for this user"
      )
    } else {
      setSelectedStartDate(rawStartDate.format("YYYY-MM-DD"))
      setSelectedEndDate(rawEndDate.format("YYYY-MM-DD"))
    }
  }, [
    startDateFromUrl,
    endDateFromUrl,
    setSelectedStartDate,
    setSelectedEndDate,
    hasHistoricalDataPermissions,
    historicalStartDateMin,
  ])

  // allow an app wide error message based on an env var
  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC_ERROR_MESSAGE) {
      toast.error(process.env.NEXT_PUBLIC_ERROR_MESSAGE, { duration: 10000 })
    }
  }, [])

  // update a users confirmation status in dynamo
  React.useEffect(() => {
    if (isAuthenticated && !userConfirmed) {
      // TODO: redirect user to the onboarding flow if not confirmed
      updateUser({ confirmed: true })
    }
  }, [isAuthenticated, userConfirmed, updateUser])

  // track the initial pageview once the user is loaded
  React.useEffect(() => {
    if (isAuthenticated) {
      configGtag({
        options: {
          page_path: window?.location?.pathname,
          custom_map: {
            dimension1: "group_id",
          },
          group_id: userGroupId,
          user_id: userId,
        },
      })
    }
  }, [isAuthenticated, userId, userEmail, userGroupId])

  // configure Sentry
  React.useEffect(() => {
    if (isAuthenticated) {
      Sentry.setUser({
        email: userEmail,
        groupId: userGroupId,
      })
    }
  }, [isAuthenticated, userEmail, userGroupId])

  // send event to signify start of user session for our own tracking
  React.useEffect(() => {
    if (
      !userEmail?.includes("luzdata.com") &&
      !userEmail?.includes("discoverluz.com") &&
      isAuthenticated &&
      !isImpersonating
    ) {
      logUserSession()
    }
    // we're purposefully not adding logUserSession to the dependency list to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isImpersonating])

  // connect user to current user in fullstory
  React.useEffect(() => {
    if (
      !userEmail?.includes("luzdata.com") &&
      !userEmail?.includes("discoverluz.com") &&
      !!userId &&
      !isImpersonating
    ) {
      configFullstory({ userId: userId, email: userEmail })
    }
  }, [userEmail, userId, isImpersonating])

  // track subsequent pageviews as history of browser changes
  React.useEffect(() => {
    const handleRouteChange = async url => {
      // give 200 ms (maybe too short?) for page title to update
      await new Promise(r => setTimeout(r, 200))
      configGtag({
        options: {
          page_path: url,
          custom_map: {
            dimension1: "group_id",
          },
          group_id: currentUser?.group?.id,
          user_id: currentUser?.id,
        },
      })
    }
    router.events.on("routeChangeComplete", handleRouteChange)
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange)
    }
  }, [router.events, currentUser])

  // render an empty shell for unauthed users, and when auth passes let them through
  let dashboardChildren = <PageLoader />
  if (currentUser?.group?.status === "disabled") {
    dashboardChildren = <DisabledDashboard />
  } else if (isAuthenticated === undefined) {
    dashboardChildren = <PageLoader />
  } else if (isAuthenticated === false) {
    const redirectTo = window?.location?.pathname
    router.push(`/auth/signin?redirectTo=${redirectTo}`)
    dashboardChildren = <PageLoader />
  } else if (isAuthenticated === true) {
    dashboardChildren = children
  } else {
    dashboardChildren = <PageLoader />
  }

  return (
    <>
      <Head>
        <title key="title">Dashboard | Luz</title>
      </Head>
      <ClientOnly
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        {isImpersonating && (
          <Box
            role="banner"
            sx={{
              position: "fixed",
              bottom: { xs: 50, sm: 0 },
              margin: "8px auto",
              width: "max-content",
              display: "flex",
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              py: 0.5,
              px: 1.5,
              borderRadius: { xs: 1, sm: 99 },
              boxShadow: 2,
              color: "gray.100",
              backgroundColor: "red.500",
              zIndex: 9999,
              isolation: `isolate`,
              fontSize: "xs",
            }}
          >
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontWeight: "semibold",
                }}
              >
                <HiExclamationCircle size={20} />{" "}
                <Box>
                  You are impersonating{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    {impersonation?.email}
                  </Box>{" "}
                  <Box
                    component="span"
                    sx={{
                      display: { xs: "none", sm: "inline-flex" },
                    }}
                  >
                    ({impersonation?.id})
                  </Box>
                </Box>
              </Box>
              <Button
                variant="white"
                size="small"
                sx={{ fontSize: "xs", py: 0.5 }}
                endIcon={<HiOutlineX />}
                onClick={() => {
                  setImpersonation(null)
                  queryClient.clear()
                }}
              >
                Stop Impersonating
              </Button>
            </>
          </Box>
        )}
      </ClientOnly>
      <Box minHeight="100vh">
        <Box display="flex" flex="1" flexDirection="column" minHeight="100vh">
          <DashboardSidebar
            sx={{
              display: { xs: "none", sm: isOnStaticPage ? "none" : "flex" },
            }}
          />
          <DashboardMobileNav sx={{ display: { xs: "flex", sm: "none" } }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              flex: 1,
              paddingLeft: {
                xs: 0,
                sm: isOnStaticPage ? "0px" : "250px",
              },
            }}
          >
            <ErrorBoundary
              FallbackComponent={
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EmptyMessage
                    type="error"
                    message="Oops, something went wrong loading the dashboard"
                  />
                </Box>
              }
            >
              {dashboardChildren}
            </ErrorBoundary>
            {isOnStaticPage ? null : <DashboardFooter />}
          </Box>
        </Box>
      </Box>
    </>
  )
}

function DisabledDashboard() {
  return (
    <Box sx={{ pt: 8 }}>
      <EmptyMessage message="Your account has been disabled. Contact Luz support for more information." />
    </Box>
  )
}
